import { matchPath, Outlet, useLocation, useNavigate } from "react-router-dom";
import Layout, { Content, Header } from "antd/es/layout/layout";
import Sider from "antd/es/layout/Sider";
import { Button, ConfigProvider, Menu, theme } from "antd";
import { ApiOutlined, ApartmentOutlined, CloudUploadOutlined, DashboardOutlined, FileTextFilled, HddFilled, KeyOutlined, LogoutOutlined, MenuFoldOutlined, MenuUnfoldOutlined, MergeFilled, ProfileOutlined, ShopFilled } from "@ant-design/icons";
import { useCallback, useMemo, useState } from "react";
import logoImg from '../../assets/images/logos/ai-measure-logo.svg'
import { handleUnauthorized, PATH_INFO } from "../util/helper";
import { useTranslation } from "react-i18next";
import { LanguageSwitcher } from "../../components/common/LanguageSwitcher";

export function PageLayout() {
  const { t } = useTranslation()
  const [collapsed, setCollapsed] = useState(false)
  const [menuOpenKeys, setMenuOpenKeys] = useState<string[]>([])
  const {
    token: { colorBgContainer, borderRadiusLG },
  } = theme.useToken()
  const navigate = useNavigate()
  const location = useLocation()

  const selectedKeys = useMemo(() => {
    const pathMap = [
      {
        key: '1-1',
        paths:
          [
            PATH_INFO.COMPANY_LIST.PATH,
            PATH_INFO.COMPANY_MANAGE.PATH,
            PATH_INFO.COMPANY_INFO.PATH,
            PATH_INFO.COMPANY_DEVICES_LIST.PATH,
            PATH_INFO.COMPANY_DEVICE_MANAGE.PATH,
            PATH_INFO.COMPANY_MEASUREMENTS_LIST.PATH,
            PATH_INFO.DEPARTMENT_LIST.PATH,
            PATH_INFO.DEPARTMENT_MANEGE.PATH,
            PATH_INFO.DEPARTMENT_DETAIL.PATH,
            PATH_INFO.DEPARTMENT_USER_DETAIL.PATH,
            PATH_INFO.DEPARTMENT_USER_MANAGE.PATH,
            PATH_INFO.MEASUREMENTS_ERROR.PATH
          ]
      },
      {
        key: '1-2',
        paths:
          [
            PATH_INFO.COMPANY_ITEM_MASTER.PATH,
            PATH_INFO.COMPANY_ITEM_MASTER_MANAGE.PATH
          ]
      },
      {
        key: '2',
        paths:
          [
            PATH_INFO.DEVICE_LIST.PATH,
            PATH_INFO.DEVICE_MANAGE.PATH
          ]
      },
      {
        key: '3',
        paths:
          [
            PATH_INFO.NAME_MATCHING.PATH
          ]
      },
      {
        key: '4',
        paths:
          [
            PATH_INFO.REPORT_LAYOUT_MASTER.PATH
          ]
      },
      {
        key: '5-1',
        paths: [PATH_INFO.CONTRACT_PLAN.PATH]
      },
      {
        key: '5-2',
        paths: [PATH_INFO.COMPANY_PLAN.PATH]
      },
      {
        key: '5-3',
        paths:
          [
            PATH_INFO.EXTERNAL_API_KEY_LIST.PATH
          ]
      },
      {
        key: '5-4',
        paths: [PATH_INFO.API_MONITORING.PATH]
      },
      {
        key: '5-5',
        paths: [PATH_INFO.BACKFILL.PATH]
      }
    ]

    const selectedKey = pathMap.find(({ paths }) =>
      paths.some((path) => matchPath({ path, end: true }, location.pathname))
    )?.key

    return selectedKey ? [selectedKey] : []
  }, [location.pathname])

  const openKeys = useMemo(() => {
    const shouldOpenCompanyMenu = selectedKeys.some((key) => key.startsWith('1-'))
    const shouldOpenApiIntegrationMenu = selectedKeys.some((key) => key.startsWith('5-'))
    const nextOpenKeys = [...menuOpenKeys]

    if (shouldOpenCompanyMenu && !nextOpenKeys.includes('1')) {
      nextOpenKeys.push('1')
    }

    if (shouldOpenApiIntegrationMenu && !nextOpenKeys.includes('5')) {
      nextOpenKeys.push('5')
    }

    return nextOpenKeys
  }, [menuOpenKeys, selectedKeys])

  const onOpenChange = useCallback((keys: string[]) => {
    setMenuOpenKeys(keys)
  }, [])

  const onClickCompany = useCallback(() => { navigate(PATH_INFO.COMPANY_LIST.PATH) }, [navigate])
  const onClickItemMaster = useCallback(() => { navigate(PATH_INFO.COMPANY_ITEM_MASTER.PATH) }, [navigate])
  const onClickDevice = useCallback(() => { navigate(PATH_INFO.DEVICE_LIST.PATH) }, [navigate])
  const onClickNameMathing = useCallback(() => { navigate(PATH_INFO.NAME_MATCHING.PATH) }, [navigate])
  const onClickReportLayoutMaster = useCallback(() => { navigate(PATH_INFO.REPORT_LAYOUT_MASTER.PATH) }, [navigate])
  const onClickExternalApiKey = useCallback(() => { navigate(PATH_INFO.EXTERNAL_API_KEY_LIST.PATH) }, [navigate])
  const onClickContractPlan = useCallback(() => { navigate(PATH_INFO.CONTRACT_PLAN.PATH) }, [navigate])
  const onClickCompanyPlan = useCallback(() => { navigate(PATH_INFO.COMPANY_PLAN.PATH) }, [navigate])
  const onClickBackfill = useCallback(() => { navigate(PATH_INFO.BACKFILL.PATH) }, [navigate])
  const onClickApiMonitoring = useCallback(() => { navigate(PATH_INFO.API_MONITORING.PATH) }, [navigate])
  const onClickLogout = useCallback(async () => {
    await handleUnauthorized(navigate)
  }, [navigate])

  return (
    <ConfigProvider
      theme={{
        token: {
          fontFamily: "Noto Sans JP"
        }
      }}
    >
      <Layout className="h-screen font-noto">
        <Sider trigger={null} collapsible collapsed={collapsed}>
          <div className='w-full h-[64px] flex items-center justify-center mb-5'>
            <img src={logoImg} alt="logo" className={`${collapsed && 'w-[65px]'}`} />
          </div>
          <div className="flex flex-col justify-between h-[calc(100%-84px)]">
            <div>
              <Menu
                theme="dark"
                mode="inline"
                selectedKeys={selectedKeys}
                openKeys={collapsed ? [] : openKeys}
                onOpenChange={onOpenChange}
                items={[
                  {
                    key: '1',
                    icon: <ShopFilled />,
                    label: t("navigation.companyManagement"),
                    children: [
                      { key: '1-1', label: t("navigation.companyList"), onClick: onClickCompany },
                      { key: '1-2', label: t("navigation.itemList"), onClick: onClickItemMaster }
                    ]
                  },
                  {
                    key: '2',
                    icon: <HddFilled />,
                    label: t("navigation.deviceManagement"),
                    onClick: onClickDevice,
                  },
                  {
                    key: '3',
                    icon: <MergeFilled />,
                    label: t("navigation.nameMatchingManagement"),
                    onClick: onClickNameMathing,
                  },
                  {
                    key: '4',
                    icon: <FileTextFilled />,
                    label: t("navigation.reportLayoutManagement"),
                    onClick: onClickReportLayoutMaster,
                  },
                  {
                    key: '5',
                    icon: <ApiOutlined />,
                    label: t("navigation.apiIntegration"),
                    children: [
                      { key: '5-1', icon: <ProfileOutlined />, label: t("navigation.contractPlanManagement"), onClick: onClickContractPlan },
                      { key: '5-2', icon: <ApartmentOutlined />, label: t("navigation.companyPlanAssignment"), onClick: onClickCompanyPlan },
                      { key: '5-3', icon: <KeyOutlined />, label: t("navigation.externalApiKeys"), onClick: onClickExternalApiKey },
                      { key: '5-4', icon: <DashboardOutlined />, label: t("navigation.apiMonitoringDashboard"), onClick: onClickApiMonitoring },
                      { key: '5-5', icon: <CloudUploadOutlined />, label: t("navigation.backfill"), onClick: onClickBackfill }
                    ]
                  },
                ]}
              />
            </div>
            <div>
              <Menu
                className="mb-auto"
                theme="dark"
                mode="inline"
                selectable={false}
                items={[
                  {
                    key: 'logout',
                    label: t("common.logout"),
                    icon: <LogoutOutlined />,
                    onClick: onClickLogout
                  }
                ]}
              />
            </div>
          </div>
        </Sider>
        <Layout className="bg-gray-300 overflow-y-auto">
          <Header style={{ padding: 0, background: colorBgContainer, position: "sticky", top: 0, zIndex: 99 }} className="shadow-lg">
            <div className="flex h-full items-center justify-between pr-4">
              <Button
                type="text"
                icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
                onClick={() => setCollapsed((prev) => !prev)}
                style={{
                  fontSize: '16px',
                  width: 64,
                  height: 64,
                }}
              />
              <LanguageSwitcher />
            </div>
          </Header>
          <Content
            style={{
              margin: '24px 16px',
              padding: 24,
              borderRadius: borderRadiusLG,
            }}
            className="bg-gray-100 min-h-fit shadow-lg"
          >
            <Outlet />
          </Content>
        </Layout>
      </Layout>
    </ConfigProvider>
  );
}
